import React, { useState, useMemo, useEffect, useCallback } from 'react'
import _cx from 'classnames/bind'
import * as graph from '@adc/sdk'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintScheduledOnlineReactiveArgs,
  primitives,
} from '@adc/blueprint'
import {
  Text,
  CurrencyInputField,
  useApplicationModals,
  BidConfirmationContent,
  // @ts-ignore no types
} from '@adc/ui-adc'
import { formatMoneyWithNumeral } from 'common/utils/formatters'
import { ZERO_DOLLARS } from 'common/components/bidBanner/constants'
import { ProxyBidTooltip } from 'common/components/bidBanner/tooltips'
import { useListingContext } from '../../../../context/listing'
import BidButton from '../bidButton'
// @ts-ignore no types
import cls from './styles.scss'
const cx = _cx.bind(cls)

type AuctionUser = primitives.AuctionUser

export const getWarningMessage = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing'],
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
) => {
  const nextProxyBidAmount = segment.__methods__.getNextProxyBidAmount(listing)
  const minimumNextBid = formatMoneyWithNumeral(
    nextProxyBidAmount,
    ZERO_DOLLARS
  )
  return `Bid amount must be at least ${minimumNextBid}.`
}

interface ProxyBidFormUIProps {
  bidAmount: number
  handleBidButtonClick: (vars: any) => void
  hasError: boolean
  isPerformingBid: boolean
  onChange: () => void
  performBid: () => void
  warning: boolean
  featureFlags: any
  user: AuctionUser
  isProxyBiddingActive: boolean
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
}

const ProxyBidFormUI = (props: ProxyBidFormUIProps) => {
  const { listing } = useListingContext()
  const {
    bidAmount,
    handleBidButtonClick: onClick,
    hasError,
    isPerformingBid,
    onChange,
    performBid,
    warning,
    featureFlags,
    user,
    isProxyBiddingActive,
    segment,
  } = props

  const { open, update } = useApplicationModals()

  const { required, acknowledged } =
    segment.__methods__.calculateOccupancyDisclaimerRequirements(listing)

  const [occupancyAcknowledged, setOccupancyAcknowledged] =
    useState(acknowledged)

  const requiresOccupancyAcknowledgment = Boolean(
    featureFlags['fe.resi.bid_confirmation_occupancy_acknowledgement'] &&
      required
  )

  const isConfirmBidButtonDisabled = Boolean(
    isPerformingBid ||
      (requiresOccupancyAcknowledgment && !occupancyAcknowledged)
  )

  const bidConfirmationModalProps = useMemo(
    () => ({
      children: (
        <BidConfirmationContent
          type={graph.enums.OnlineAuctionSegmentBidType.MaxBid}
          listing={listing}
          segment={segment}
          user={user}
          bidAmount={bidAmount}
          isPerformingBid={isPerformingBid}
          isOccupancyDisclaimerRequired={requiresOccupancyAcknowledgment}
          occupancyDisclaimerAcknowledged={occupancyAcknowledged}
          setOccupancyAcknowledged={setOccupancyAcknowledged}
        />
      ),
      elmId: 'pdp_bid_confirmation_modal',
      size: 'tiny',
      title: 'Confirm Bid',
      confirmButton: {
        onClick: performBid,
        disabled: isConfirmBidButtonDisabled,
      },
    }),
    [
      user,
      listing,
      segment,
      occupancyAcknowledged,
      setOccupancyAcknowledged,
      isPerformingBid,
      bidAmount,
      isConfirmBidButtonDisabled,
      requiresOccupancyAcknowledgment,
      performBid,
    ]
  )

  useEffect(() => {
    update(bidConfirmationModalProps)
  }, [bidConfirmationModalProps, update])

  const openBidConfirmationModal = useCallback(() => {
    open('confirmation', bidConfirmationModalProps)
  }, [open, bidConfirmationModalProps])

  return (
    <div>
      <div className={cx('u-mb-6')}>
        <div className={cx('label-value')}>
          <Text color='gray' content='Bid Up To' />
          <CurrencyInputField
            elmId='place_bid'
            onChange={onChange}
            value={
              bidAmount ? formatMoneyWithNumeral(bidAmount, ZERO_DOLLARS) : ''
            }
            className={cx('bid-input', { warning })}
            hideWrapper
            allowDecimal={false}
          />
        </div>
        {warning && (
          <Text
            className={cx('u-mt-1', 'u-text-right')}
            data-elm-id='place_bid_input_error'
            color='error'
            size='tiny'
            content={getWarningMessage(listing, segment)}
          />
        )}
      </div>
      <div className={cx('u-mb-6')}>
        <BidButton
          isProxyBiddingActive={isProxyBiddingActive}
          bidAmount={bidAmount}
          hasError={hasError}
          onClick={() => onClick(openBidConfirmationModal)}
        />
      </div>
      <div className={cx('centered-section')}>
        <ProxyBidTooltip />
      </div>
    </div>
  )
}

export default ProxyBidFormUI
